import { useState } from 'react';

// ---------------------------------------------------------------------------
// DualAmortizationView — side-by-side FAM co-ownership vs traditional lease
// ---------------------------------------------------------------------------

interface DualAmortizationViewProps {
  vehiclePrice: number;
  termMonths: number;
  monthlyPayment: number;
  creatorOwnershipPct: number;
  unitPrice: number;
}

interface AmortRow {
  month: number;
  usageFee: number;
  ownershipPurchase: number;
  creatorPct: number;
}

interface LeaseRow {
  month: number;
  leasePayment: number;
  ownershipPct: number;
}

function buildFamSchedule(
  vehiclePrice: number,
  termMonths: number,
  monthlyPayment: number,
  creatorOwnershipPct: number,
): AmortRow[] {
  const rows: AmortRow[] = [];
  let currentPct = creatorOwnershipPct;
  const investorPct = 1 - creatorOwnershipPct;
  const monthlyBuybackBase = investorPct > 0 ? (investorPct * vehiclePrice) / termMonths : 0;

  for (let m = 1; m <= termMonths; m++) {
    const remainingInvestorPct = Math.max(0, 1 - currentPct);
    // Usage fee proportional to unowned share, decreasing
    const usageFee = remainingInvestorPct * vehiclePrice * 0.015;
    const ownershipPurchase = Math.min(monthlyPayment - usageFee, monthlyBuybackBase);
    const pctGained = vehiclePrice > 0 ? ownershipPurchase / vehiclePrice : 0;
    currentPct = Math.min(1, currentPct + pctGained);

    rows.push({
      month: m,
      usageFee: Math.max(0, usageFee),
      ownershipPurchase: Math.max(0, ownershipPurchase),
      creatorPct: currentPct,
    });
  }
  return rows;
}

function buildLeaseSchedule(vehiclePrice: number, termMonths: number): LeaseRow[] {
  const leaseRate = vehiclePrice * 0.015;
  const rows: LeaseRow[] = [];
  for (let m = 1; m <= termMonths; m++) {
    rows.push({ month: m, leasePayment: leaseRate, ownershipPct: 0 });
  }
  return rows;
}

function fmt(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

export function DualAmortizationView({
  vehiclePrice,
  termMonths,
  monthlyPayment,
  creatorOwnershipPct,
  unitPrice,
}: DualAmortizationViewProps) {
  const [showAll, setShowAll] = useState(false);

  const famSchedule = buildFamSchedule(vehiclePrice, termMonths, monthlyPayment, creatorOwnershipPct);
  const leaseSchedule = buildLeaseSchedule(vehiclePrice, termMonths);

  const displayRows = showAll ? famSchedule : famSchedule.slice(0, 12);
  const leaseDisplayRows = showAll ? leaseSchedule : leaseSchedule.slice(0, 12);

  const famTotalCost = famSchedule.reduce((s, r) => s + r.usageFee + r.ownershipPurchase, 0);
  const leaseTotalCost = leaseSchedule.reduce((s, r) => s + r.leasePayment, 0);
  const leaseResidual = vehiclePrice * 0.45;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span>Vehicle: {fmt(vehiclePrice)}</span>
        <span>Term: {termMonths} mo</span>
        <span>Unit price: {fmt(unitPrice)}</span>
        <span>Starting ownership: {fmtPct(creatorOwnershipPct)}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* FAM Co-Ownership Schedule */}
        <div className="border border-indigo-100 rounded-xl overflow-hidden">
          <div className="bg-indigo-50 px-4 py-2.5 border-b border-indigo-100">
            <h4 className="text-sm font-bold text-indigo-900">FAM Co-Ownership</h4>
            <p className="text-xs text-indigo-600">You build ownership every month</p>
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-100">
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Mo</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Usage Fee</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Ownership Purchase</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Your Ownership</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {displayRows.map((r) => (
                  <tr key={r.month} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-gray-700 font-medium">{r.month}</td>
                    <td className="px-3 py-1.5 text-right text-gray-600">{fmt(r.usageFee)}</td>
                    <td className="px-3 py-1.5 text-right text-indigo-600 font-medium">{fmt(r.ownershipPurchase)}</td>
                    <td className="px-3 py-1.5 text-right text-emerald-600 font-semibold">{fmtPct(r.creatorPct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Traditional Lease */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
            <h4 className="text-sm font-bold text-gray-800">Traditional Lease</h4>
            <p className="text-xs text-gray-500">No ownership at end of term</p>
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-100">
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Mo</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Lease Payment</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Your Ownership</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {leaseDisplayRows.map((r) => (
                  <tr key={r.month} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-gray-700 font-medium">{r.month}</td>
                    <td className="px-3 py-1.5 text-right text-gray-600">{fmt(r.leasePayment)}</td>
                    <td className="px-3 py-1.5 text-right text-red-400 font-semibold">{fmtPct(r.ownershipPct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {termMonths > 12 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
        >
          {showAll ? 'Show first 12 months' : `Show all ${termMonths} months`}
        </button>
      )}

      {/* Summary comparison */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
          <p className="text-xs text-indigo-600 font-medium">FAM Total Cost</p>
          <p className="text-lg font-bold text-indigo-900">{fmt(famTotalCost)}</p>
          <p className="text-xs text-indigo-500 mt-1">You own 100% at end</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <p className="text-xs text-gray-600 font-medium">Lease Total Cost</p>
          <p className="text-lg font-bold text-gray-800">{fmt(leaseTotalCost)}</p>
          <p className="text-xs text-gray-500 mt-1">You own 0% at end</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
          <p className="text-xs text-emerald-600 font-medium">Buyout to Own (Lease)</p>
          <p className="text-lg font-bold text-emerald-800">{fmt(leaseTotalCost + leaseResidual)}</p>
          <p className="text-xs text-emerald-500 mt-1">Lease + {fmt(leaseResidual)} residual</p>
        </div>
      </div>
    </div>
  );
}
